import { useCallback, useMemo } from 'react';
import { AutoPricingUnderCutModeSelector } from 'src/components/AutoPricing/AutoPricingUnderCutModeSelector/AutoPricingUnderCutModeSelector';
import { AutoPricingUnderCutReferenceListingPositionInput } from 'src/components/AutoPricing/AutoPricingUnderCutReferenceListingPositionInput/AutoPricingUnderCutReferenceListingPositionInput';
import { Content } from 'src/contexts/ContentContext';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Stack } from 'src/core/ui';
import { ToggleGroup } from 'src/core/ui/ToggleGroup/ToggleGroup';
import { useUndercutAmountSettings } from 'src/hooks/useUndercutAmountSettings';
import {
  getUndercutPriceRepresentingMode,
  UNDERCUT_PRICE_MODE_OPTIONS,
  UndercutPriceSettingsMode,
} from 'src/utils/autoPricingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { roundToPrecision } from 'src/utils/numberFormatter';
import { AutoPricingUndercutMode } from 'src/WebApiController';

import { AutoPricingSettingsInput } from '../../BulkEditAutoPricingSettingsDialog.types';
import { useAutoPricingSettings } from '../../useBulkAutoPricingSettings';
import * as styles from '../AutoPricingSettings.css';
import { PricingSetting } from '../PricingSetting';
import { SettingsSubheader } from '../PricingSubheader';

type PricingUndercuttingProps = {
  origin: Pick<
    AutoPricingSettingsInput,
    | 'undercutMode'
    | 'undercutAbsoluteAmount'
    | 'undercutRelativeAmount'
    | 'undercutReferenceListingPosition'
  >;
  disabled: boolean;
};

export const PricingUndercutting = ({
  origin,
  disabled,
}: PricingUndercuttingProps) => {
  const {
    uiCurrency,
    undercutMode,
    undercutRelativeAmount,
    undercutAbsoluteAmount,
    undercutReferenceListingPosition,
    onUndercutModeChange,
    onUndercutChange,
  } = useAutoPricingSettings();

  const undercutModeHasChange = useMemo(() => {
    return (
      origin.undercutMode?.value !== undercutMode?.value ||
      origin.undercutReferenceListingPosition?.value !==
        undercutReferenceListingPosition?.value
    );
  }, [
    origin.undercutMode?.value,
    undercutMode?.value,
    origin.undercutReferenceListingPosition?.value,
    undercutReferenceListingPosition?.value,
  ]);

  const showReferenceListingPositionInput = useMemo(() => {
    return (
      undercutModeHasChange &&
      (undercutMode?.value !== AutoPricingUndercutMode.Simple ||
        !!undercutReferenceListingPosition?.value)
    );
  }, [
    undercutMode?.value,
    undercutModeHasChange,
    undercutReferenceListingPosition?.value,
  ]);

  const onResetUndercutMode = useCallback(() => {
    onUndercutModeChange({
      undercutModeNew: origin.undercutMode?.value,
      undercutModeHasChangedNew: false,
      undercutReferenceListingPositionNew:
        origin.undercutReferenceListingPosition?.value,
      undercutReferenceListingPositionHasChangedNew: false,
    });
  }, [
    onUndercutModeChange,
    origin.undercutMode?.value,
    origin.undercutReferenceListingPosition?.value,
  ]);

  const {
    undercutAbsoluteAmountMode,
    setUndercutAbsoluteAmountMode,
    undercutRelativeAmountMode,
    setUndercutRelativeAmountMode,
  } = useUndercutAmountSettings(
    undercutAbsoluteAmount?.value,
    undercutRelativeAmount?.value
  );

  const undercutAmountHasChange = useMemo(
    () =>
      origin.undercutAbsoluteAmount?.value !== undercutAbsoluteAmount?.value ||
      origin.undercutRelativeAmount?.value !== undercutRelativeAmount?.value,
    [
      origin.undercutAbsoluteAmount?.value,
      origin.undercutRelativeAmount?.value,
      undercutAbsoluteAmount?.value,
      undercutRelativeAmount?.value,
    ]
  );
  const onResetUndercutAmount = useCallback(
    () =>
      onUndercutChange(
        undefined,
        origin.undercutAbsoluteAmount?.value,
        origin.undercutRelativeAmount?.value
      ),
    [
      onUndercutChange,
      origin.undercutAbsoluteAmount?.value,
      origin.undercutRelativeAmount?.value,
    ]
  );

  return (
    <>
      <SettingsSubheader title={ContentId.Undercutting} />

      <PricingSetting
        header={<Content id={ContentId.SetReferenceListingTo} />}
        detail={
          <AutoPricingUnderCutModeSelector
            undercutMode={undercutMode?.value ?? null}
            undercutReferenceListingPosition={
              undercutReferenceListingPosition?.value ?? null
            }
            setUndercutModeValues={(
              newMode: AutoPricingUndercutMode | null,
              newPosition: number | null
            ) => {
              onUndercutModeChange({
                undercutModeNew: newMode,
                undercutReferenceListingPositionNew: newPosition,
              });
            }}
            disabled={disabled}
          />
        }
        hasChange={undercutModeHasChange}
        hasConflict={
          origin.undercutMode?.hasConflict ||
          origin.undercutReferenceListingPosition?.hasConflict
        }
        onReset={onResetUndercutMode}
      />

      {showReferenceListingPositionInput && (
        <PricingSetting
          header={<Content id={ContentId.ReferenceThe} />}
          detail={
            <AutoPricingUnderCutReferenceListingPositionInput
              undercutMode={undercutMode?.value ?? null}
              undercutReferenceListingPosition={
                undercutReferenceListingPosition?.value ?? null
              }
              setUndercutReferenceListingPosition={(
                newPosition: number | null
              ) => {
                onUndercutModeChange({
                  undercutModeNew: undefined,
                  undercutReferenceListingPositionNew: newPosition,
                });
              }}
              disabled={disabled}
            />
          }
        />
      )}

      <PricingSetting
        header={<Content id={ContentId.UnderCutModePrompt} />}
        detail={
          <Stack gap="m">
            <ToggleGroup
              disabled={!undercutMode || !undercutRelativeAmount?.value}
              options={UNDERCUT_PRICE_MODE_OPTIONS}
              value={undercutRelativeAmountMode}
              onValueChange={(value) => {
                if (!value) return;

                const mode = value as UndercutPriceSettingsMode;
                setUndercutRelativeAmountMode(mode);

                if (undercutRelativeAmount?.value) {
                  const curAmountAbs = Math.abs(undercutRelativeAmount.value);
                  onUndercutChange(
                    undefined,
                    undefined,
                    getUndercutPriceRepresentingMode(curAmountAbs, mode)
                  );
                }
              }}
            />

            <div className={styles.inlinePricingInputText}>
              <Content id={ContentId.PriceBy} />
            </div>

            <PosFormField style={{ width: 'fit-content' }}>
              <PosTextField
                disabled={!undercutMode}
                value={
                  undercutRelativeAmount?.value != null
                    ? roundToPrecision(
                        Math.abs(undercutRelativeAmount.value) * 100,
                        8
                      )
                    : ''
                }
                placeholder={undefined}
                type="number"
                postfixDisplay="%"
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (v >= 0 && v <= Number.MAX_VALUE) {
                    const relativeValue = Math.min(v, 100) / 100;
                    if (
                      undercutRelativeAmountMode ===
                      UndercutPriceSettingsMode.Premium
                    ) {
                      onUndercutChange(undefined, undefined, -relativeValue);
                    } else {
                      onUndercutChange(undefined, undefined, relativeValue);
                    }
                  } else {
                    onUndercutChange(undefined, undefined, null);
                  }
                }}
              />
            </PosFormField>
            <div className={styles.inlinePricingInputText}>
              <Content id={ContentId.Then} />
            </div>
            <ToggleGroup
              disabled={!undercutMode || !undercutAbsoluteAmount}
              options={UNDERCUT_PRICE_MODE_OPTIONS}
              value={undercutAbsoluteAmountMode}
              onValueChange={(value) => {
                if (!value) return;

                const mode = value as UndercutPriceSettingsMode;
                setUndercutAbsoluteAmountMode(mode);

                if (undercutAbsoluteAmount?.value) {
                  const curAmountAbs = Math.abs(undercutAbsoluteAmount.value);
                  onUndercutChange(
                    undefined,
                    getUndercutPriceRepresentingMode(curAmountAbs, mode)
                  );
                }
              }}
            />
            <div className={styles.inlinePricingInputText}>
              <Content id={ContentId.By} />
            </div>
            <PosFormField style={{ width: 'fit-content' }}>
              <PosCurrencyField
                uiCurrency={uiCurrency}
                disabled={!undercutMode}
                value={
                  undercutAbsoluteAmount?.value != null
                    ? Math.abs(undercutAbsoluteAmount.value)
                    : ''
                }
                placeholder={undefined}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (v >= 0 && v <= Number.MAX_VALUE) {
                    if (
                      undercutAbsoluteAmountMode ===
                      UndercutPriceSettingsMode.Premium
                    ) {
                      onUndercutChange(undefined, -v);
                    } else {
                      onUndercutChange(undefined, v);
                    }
                  } else {
                    onUndercutChange(undefined, null);
                  }
                }}
              />
            </PosFormField>
          </Stack>
        }
        hasChange={undercutAmountHasChange}
        hasConflict={
          origin.undercutAbsoluteAmount?.hasConflict ||
          origin.undercutRelativeAmount?.hasConflict
        }
        onReset={onResetUndercutAmount}
      />
    </>
  );
};
